import { combineReducers, configureStore, freeze } from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/query'
import { rememberEnhancer, rememberReducer, REMEMBER_PERSISTED, REMEMBER_REHYDRATED } from 'redux-remember'
import accessRightsReducer from 'store/access-rights/slice'
import appReducer from 'store/app/slice'
import authReducer from 'store/auth/slice'
import contactReducer from 'store/contact/slice'
import salesReducer from 'store/sales/slice'
import { boltBackend } from 'store/queries/bolt-backend'
import { boltApi } from 'store/queries/bolt-api'
import { cmsApi } from 'store/queries/cms-api'
import { IS_PROD_OR_BETA } from 'constants/envs.ts'
import { accessRightsListenerMiddleware } from 'store/access-rights/listeners'
import { loggingReduxEnhancer } from 'utils/logging.ts'
import { initialAuthState } from 'store/auth/initial'
import { initialContactState } from 'store/contact/initial'

// Reducer keys persisted to sessionStorage by redux-remember
const rememberedKeys = ['auth', 'contact']

// Initial states of the persisted slices, merged under the stored state on rehydration
// so fields added after the state was stored keep their defaults
const initialPersistedStates: Record<string, object> = {
  auth: initialAuthState,
  contact: initialContactState
}

const rootReducer = rememberReducer(
  combineReducers({
    accessRights: accessRightsReducer,
    app: appReducer,
    auth: authReducer,
    contact: contactReducer,
    sales: salesReducer,
    [boltApi.reducerPath]: boltApi.reducer,
    [boltBackend.reducerPath]: boltBackend.reducer,
    [cmsApi.reducerPath]: cmsApi.reducer
  })
)

export const store = configureStore({
  reducer: rootReducer,
  devTools: !IS_PROD_OR_BETA,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore the redux-remember actions as their payload is the (potentially large) root state
        ignoredActions: [REMEMBER_REHYDRATED, REMEMBER_PERSISTED]
      }
    }).concat([boltApi.middleware, boltBackend.middleware, cmsApi.middleware, accessRightsListenerMiddleware.middleware]),
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers()
      .concat(loggingReduxEnhancer)
      .prepend(
        rememberEnhancer(window.sessionStorage, rememberedKeys, {
          // Deep frozen because the rehydrated state bypasses immer (no slice handles REMEMBER_REHYDRATED), so without this
          // it stays mutable until the first dispatch freezes it in place. proxy-memoize caches per object whether it needed
          // a configurable copy the first time it proxied it, so an object that gets frozen after that throws a proxy
          // invariant TypeError on the next cache miss
          unserialize: (data, key) => freeze({ ...initialPersistedStates[key], ...JSON.parse(data) }, true)
        })
      )
})

setupListeners(store.dispatch)

// Infer the `RootState` and `StoreDispatch` types from the store itself
export type RootState = ReturnType<typeof rootReducer>
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type StoreDispatch = typeof store.dispatch
export type RootStore = typeof store
